import { useMemo } from "react";
import { GetStandardProductOrderResponse } from "shared/api/middleware-api-client";
import { Delete } from "shared/ui/icons/Delete";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useOrderContext } from "../../app/providers/OrderProvider";
import { usePaymentContext } from "../../pages/payment/providers/PaymentProvider";
import { useSearchParams } from "react-router-dom";
import { useVoucherOrderAddition } from "app/hooks/shared/useVoucherOrderAddition";
import LoadingLogo from "features/shared/loading-logo";

interface StandardOrderPriceTableProps {
  className?: string;
  entity: GetStandardProductOrderResponse;
}

const StandardOrderPriceTable = ({
  className,
  entity,
}: StandardOrderPriceTableProps) => {
  const orderContext = useOrderContext();
  const [searchParams] = useSearchParams();

  // Standard delivery price
  const { standardOrderDeliveryPrice } = usePaymentContext();

  // Voucher helpers
  const orderID = searchParams.get("pOrderId");
  const { addVoucherToOrder, status } = useVoucherOrderAddition({
    orderID,
    orderType: "p",
  });

  const voucherLoading = status === "loading";

  const discountRecords = useMemo(() => {
    if (!entity.discountRecords || entity.discountRecords.length <= 0) {
      return [];
    }

    return entity.discountRecords.map((record) => ({
      ...record,
      price: orderContext.getReadableDiscountRecordPrice(record),
    }));
  }, [entity, orderContext]);

  // Remove voucher from order
  const removeVoucherClick = async () => {
    await addVoucherToOrder("");
  };

  return (
    <div
      className={concatClassnames(
        "gg-flex gg-flex-col gg-gap-y-2.5 lg:gg-w-1/3 xl:gg-w-full 2xl:gg-w-[45%]",
        className,
      )}
    >
      {voucherLoading && (
        <div className="gg-absolute gg-inset-0 gg-z-10 gg-grid gg-h-full gg-w-full gg-place-items-center gg-backdrop-blur-lg">
          <div
            className="gg-motion-reduce:animate-[spin_1.5s_linear_infinite] gg-inline-block gg-h-16 gg-w-16 gg-animate-spin gg-rounded-full gg-border-4 gg-border-solid gg-border-current gg-border-r-transparent gg-align-[-0.125em]"
            role="status"
          ></div>
        </div>
      )}
      {/* Subtotaal */}
      <div className="gg-flex gg-justify-between gg-gap-x-4">
        <Text>Subtotaal</Text>
        <Text bold>{getFormattedPrice(entity.subtotalPrice)}</Text>
      </div>

      {/* Delivery price */}
      {standardOrderDeliveryPrice > 0 && (
        <div className="gg-flex gg-justify-between gg-gap-x-4">
          <Text>Verzendkosten</Text>
          <Text bold>{getFormattedPrice(standardOrderDeliveryPrice)}</Text>
        </div>
      )}

      {/* {discountRecords.length > 0 && ( */}
      {/* <div className="gg-space-y-2 gg-mt-2 "> */}
      {/* <h3 className="gg-font-bold gg-text-lg ">Korting</h3> */}
      {discountRecords.map((record) => (
        <div key={record.id} className="gg-flex gg-justify-between gg-gap-x-4">
          <Text className="gg-relative">
            Voucher:{" "}
            <span className="gg-font-bold gg-uppercase">{record.title}</span>
            <button
              onClick={removeVoucherClick}
              data-testid="remove-standard-order-voucher-button"
              className="gg-absolute -gg-right-6 gg-inline-flex gg-h-full gg-items-center"
              aria-label="Remove voucher"
            >
              <Delete />
            </button>
          </Text>
          <Text bold className="gg-text-green-300">
            {record.price}
          </Text>
        </div>
      ))}
      {/* </div> */}
      {/* )} */}
    </div>
  );
};

export default StandardOrderPriceTable;
